import React, { useEffect, useState } from "react";
import "../styles/cart.css";
import CartItem from "../components/CartItem";
import OrderSummary from "../components/Ordersummary";
import { useDispatch, useSelector } from "react-redux";
import { fetchCart, clearCart } from "../actions/cartActions";
import { fetchShippingFees } from "../actions/ShippingFeesActions";
import {
  applyCoupon,
  removeCoupon,
  clearCoupons,
} from "../actions/couponActions";
import { useNavigate } from "react-router-dom";
import { useImage } from "../contexts/ImageContext";
import emptycart from "../assets/images/emptycart.jpg";
import Loader from "../components/Loader";
import { CartPageSkeleton } from "../components/HomeSkeletons";
import Recommendations from "../components/Recommendations";
import ProgressIndicator from "../components/ProgressIndicator";

const Cart = () => {
  useEffect(() => {
    document.title = "Cart";
  }, []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponSuccess, setCouponSuccess] = useState("");

  const { isAuthenticated, isLoading: authLoading } = useSelector(
    (state) => state.auth,
  );
  const { items: cartItems = [], loading: cartLoading } = useSelector(
    (state) => state.cart,
  );
  const {
    appliedCoupons = [],
    loading: couponLoading,
    error: couponErrorState,
  } = useSelector((state) => state.coupons);
  const { getImageUrl } = useImage();

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCart(getImageUrl)).catch(() => {
        setHasError(true);
        dispatch(clearCart());
      });
      dispatch(fetchShippingFees());
    } else {
      dispatch(clearCart());
    }
  }, [dispatch, isAuthenticated, getImageUrl]);

  // Auto-apply routine coupon from localStorage
  useEffect(() => {
    const routineCoupon = localStorage.getItem("idp_routineCoupon");
    if (routineCoupon && isAuthenticated && appliedCoupons.length === 0) {
      // Apply the routine coupon automatically
      dispatch(applyCoupon(routineCoupon)).then((result) => {
        if (result.success) {
          setCouponSuccess(
            `Routine discount "${routineCoupon}" applied automatically!`,
          );
          // Clear the stored coupon after applying
          localStorage.removeItem("idp_routineCoupon");
        }
      });
    }
  }, [dispatch, isAuthenticated, appliedCoupons.length]);

  if (authLoading || cartLoading) {
    return (
      <div className="cart-page">
        <ProgressIndicator currentStep={1} />
        <CartPageSkeleton />
      </div>
    );
  }

  if (hasError) {
    return <Loader />;
  }

  const cartTotal = cartItems.reduce(
    (total, item) =>
      total + (parseFloat(item.product.price) || 0) * (item.quantity || 1),
    0,
  );

  const showEmptyCart = (!cartLoading && cartItems.length === 0) || hasError;

  // Coupon handling functions
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    setCouponError("");
    setCouponSuccess("");

    const result = await dispatch(applyCoupon(couponCode.trim()));

    if (result.success) {
      setCouponSuccess(`Coupon "${couponCode}" applied successfully!`);
      setCouponCode("");
    } else {
      setCouponError(result.error);
    }
  };

  const handleRemoveCoupon = (couponCode) => {
    dispatch(removeCoupon(couponCode));
    setCouponError("");
    setCouponSuccess("");
  };

  const handleCouponInputChange = (e) => {
    setCouponCode(e.target.value);
    setCouponError("");
    setCouponSuccess("");
  };

  return (
    <div className="cart-page">
      <ProgressIndicator currentStep={1} />

      <div className="cart-container">
        <div className="cart-items-section">
          <h2 className="cart-title">Shopping Cart</h2>
          {cartItems.length > 0 ? (
            <div className="cart-items-list">
              {cartItems.map((item) => (
                <CartItem
                  key={item.cart_item_id}
                  cartItemId={item.cart_item_id}
                  product={{
                    productId: item.product.product_id,
                    product_id: item.product.product_id,
                    name: item.product.name,
                    description: (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: item.product.description,
                        }}
                      />
                    ),
                    price: item.product.price,
                    quantity: item.quantity,
                    imageUrl: item.product.imageUrl || "default-image-url",
                    selected_color: item.selected_color,
                    size_name: item.size?.name,
                  }}
                  CartItem={item}
                />
              ))}
            </div>
          ) : (
            showEmptyCart && (
              <div className="empty-cart">
                <img
                  src={emptycart}
                  alt="Empty Cart"
                  className="empty-cart-image"
                />

                <h2 className="empty-cart-title">Your cart is empty</h2>
                <p className="empty-cart-text">
                  Browse our products and find something you'll love!
                </p>
                <button
                  className="continue-shopping-btn"
                  onClick={() => navigate("/")}
                >
                  Continue Shopping
                </button>
              </div>
            )
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="cart-sidebar">
            <div className="promos-section">
              <h4>Coupons</h4>

              {/* Coupon Input */}
              <div className="coupon-input-section">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  className="promo-input"
                  value={couponCode}
                  onChange={handleCouponInputChange}
                  disabled={couponLoading}
                />
                <button
                  className="apply-promo-button"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading}
                >
                  {couponLoading ? "Applying..." : "Apply"}
                </button>
              </div>

              {/* Coupon Messages */}
              {couponError && <div className="coupon-error">{couponError}</div>}
              {couponSuccess && (
                <div className="coupon-success">{couponSuccess}</div>
              )}

              {/* Applied Coupons */}
              {appliedCoupons.length > 0 && (
                <div className="applied-coupons">
                  <h5>Applied Coupon:</h5>
                  {appliedCoupons.map((coupon) => (
                    <div key={coupon.coupon_id} className="applied-coupon">
                      <span className="coupon-code">{coupon.coupon_code}</span>
                      <span className="coupon-discount">
                        {coupon.coupon_type === "percentage"
                          ? `${parseFloat(coupon.discount).toFixed(0)}% off`
                          : `RWF ${coupon.discount} off`}
                      </span>
                      <button
                        className="remove-coupon-btn"
                        onClick={() => handleRemoveCoupon(coupon.coupon_code)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <OrderSummary
              cartTotal={cartTotal}
              appliedCoupons={appliedCoupons}
            />
          </div>
        )}
      </div>
      <div className="you-might-like-section">
        <Recommendations />
      </div>
    </div>
  );
};

export default Cart;
